/**
 * SegmentDashboardPage — `/segments/:id`
 *
 * Detalle de un segmento guardado. Muestra:
 *  - Hero con emoji + nombre + status + count grande
 *  - Resumen del filtro (conversationSummary + chips de campos filtrados)
 *  - Campañas que lo usan (busca en objectives.segmentId)
 *  - Acciones: usar en nueva campaña · editar · duplicar · borrar
 */

import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { FIELD_LABELS } from "../fieldLabels";
import {
    ArrowLeft,
    Users,
    Calendar,
    Loader2,
    AlertCircle,
    Send,
    Copy,
    Trash2,
    Filter,
    MessageSquare,
    Target,
    Sparkles,
} from "lucide-react";
import { Button } from "../../../shared/components/ui/Button";
import { ConfirmDangerModal } from "../../../shared/components/ui/ConfirmDangerModal";
import { toast } from "../../../shared/hooks/useToast";
import { segmentsApi } from "../api";
import { objectivesApi, STATUS_META } from "../../objectives/api";
import { PRESET_BY_KEY } from "../../objectives/types";
import type { Segment } from "../types";
import type { Objective } from "../../objectives/types";

const MOCK_BRAND_ID = "brand-demo";

export function SegmentDashboardPage() {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();

    const [segment, setSegment] = useState<Segment | null>(null);
    const [campaigns, setCampaigns] = useState<Objective[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (!id) return;
        (async () => {
            setLoading(true);
            const [segRes, objRes] = await Promise.all([
                segmentsApi.get(id, MOCK_BRAND_ID),
                objectivesApi.list(MOCK_BRAND_ID),
            ]);
            if (!segRes.success || !segRes.data) {
                setError(segRes.error || "Segmento no encontrado");
            } else {
                setSegment(segRes.data);
                if (objRes.success) {
                    setCampaigns(
                        objRes.data.filter((o) => o.segmentId === id),
                    );
                }
            }
            setLoading(false);
        })();
    }, [id]);

    const [duplicating, setDuplicating] = useState(false);
    const handleDuplicate = async () => {
        if (!segment) return;
        setDuplicating(true);
        const res = await segmentsApi.duplicate(segment.id, MOCK_BRAND_ID);
        setDuplicating(false);
        if (res.success && res.data) {
            toast.success("Segmento duplicado — editando la copia");
            navigate(`/segments/${res.data.id}`);
        } else {
            toast.error("No se pudo duplicar el segmento");
        }
    };

    const [confirmingDelete, setConfirmingDelete] = useState(false);
    const [deleteBusy, setDeleteBusy] = useState(false);
    const handleDelete = () => {
        if (!segment) return;
        setConfirmingDelete(true);
    };
    const confirmDelete = async () => {
        if (!segment) return;
        setDeleteBusy(true);
        const res = await segmentsApi.delete(segment.id, MOCK_BRAND_ID);
        setDeleteBusy(false);
        setConfirmingDelete(false);
        if (res.success) {
            toast.success("Segmento eliminado");
            navigate("/segments");
        } else {
            toast.error("No se pudo eliminar el segmento");
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center text-slate-500 text-sm">
                <Loader2 size={18} className="animate-spin mr-2" />
                Cargando segmento…
            </div>
        );
    }
    if (error || !segment) {
        return (
            <div className="min-h-screen flex flex-col items-center justify-center gap-3">
                <AlertCircle size={32} className="text-red-500" />
                <p className="text-sm text-red-600">
                    {error || "Segmento no encontrado"}
                </p>
                <Link
                    to="/segments"
                    className="text-xs text-violet-600 underline"
                >
                    Volver a segmentos
                </Link>
            </div>
        );
    }

    const emoji = (segment as any).emoji || "🎯";
    const isDraft = segment.status === "draft";
    const lastUsedAt = (segment as any).lastUsedAt as string | undefined;
    const totalReach = segment.lastKnownCount || 0;
    const filteredFields = segment.filteredFields || [];

    return (
        <div className="min-h-screen p-6 md:p-8 animate-fade-in">
            <div className="mx-auto max-w-6xl space-y-6">
                {/* Breadcrumb */}
                <Link
                    to="/segments"
                    className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-violet-600 transition-colors"
                >
                    <ArrowLeft size={14} />
                    Volver a segmentos
                </Link>

                {/* ── Hero ── */}
                <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-6">
                    <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-5">
                        <div className="flex items-start gap-4 min-w-0 flex-1">
                            <div className="text-5xl leading-none flex-shrink-0">
                                {emoji}
                            </div>
                            <div className="min-w-0 flex-1">
                                <div className="flex items-center gap-2 mb-1.5 flex-wrap">
                                    {isDraft ? (
                                        <span className="text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full bg-amber-100 text-amber-700">
                                            Borrador
                                        </span>
                                    ) : (
                                        <span className="text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700">
                                            Guardado
                                        </span>
                                    )}
                                </div>
                                <h1 className="text-3xl font-bold text-slate-900 tracking-tight">
                                    {segment.name || "Segmento sin nombre"}
                                </h1>
                                <p className="text-sm text-slate-500 mt-2 leading-relaxed">
                                    {segment.conversationSummary ||
                                        "Sin descripción"}
                                </p>
                            </div>
                        </div>

                        {/* Big count */}
                        <div className="text-center bg-violet-50 rounded-2xl px-6 py-4 flex-shrink-0 border border-violet-100">
                            <p className="text-[10px] uppercase tracking-wider font-bold text-violet-700 mb-1">
                                Alcance
                            </p>
                            <p className="text-4xl font-black text-violet-700 tabular-nums leading-none">
                                {totalReach.toLocaleString("es-AR")}
                            </p>
                            <p className="text-[11px] text-violet-600 mt-1">
                                usuarios
                            </p>
                        </div>
                    </div>

                    {/* Acciones primarias */}
                    <div className="flex flex-wrap gap-2 mt-5">
                        <Link to="/objectives/new">
                            <Button variant="primary">
                                <Send size={15} />
                                Usar en nueva campaña
                            </Button>
                        </Link>
                        <Button
                            variant="secondary"
                            onClick={handleDuplicate}
                            disabled={duplicating}
                        >
                            {duplicating ? (
                                <Loader2 size={15} className="animate-spin" />
                            ) : (
                                <Copy size={15} />
                            )}
                            Duplicar
                        </Button>
                        <Button
                            variant="secondary"
                            onClick={handleDelete}
                            className="!text-red-600 !border-red-200 hover:!bg-red-50"
                        >
                            <Trash2 size={15} />
                            Borrar
                        </Button>
                    </div>
                </div>

                {/* ── 2 columnas: filtros + campañas ── */}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    {/* Filtros aplicados */}
                    <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5">
                        <div className="flex items-center gap-2 mb-4">
                            <div className="p-1.5 rounded-lg bg-violet-50 text-violet-600">
                                <Filter size={14} />
                            </div>
                            <h2 className="text-sm font-bold text-slate-900">
                                Filtros aplicados
                            </h2>
                        </div>

                        {filteredFields.length === 0 ? (
                            <p className="text-xs text-slate-500 italic">
                                Sin filtros explícitos — incluye toda la audiencia.
                            </p>
                        ) : (
                            <div className="space-y-1.5">
                                {filteredFields.map((field) => (
                                    <div
                                        key={field}
                                        className="flex items-center gap-2 px-3 py-2 bg-slate-50 rounded-lg"
                                    >
                                        <span className="w-1.5 h-1.5 rounded-full bg-violet-500" />
                                        <span className="text-xs text-slate-700">
                                            {FIELD_LABELS[field] || field}
                                        </span>
                                    </div>
                                ))}
                            </div>
                        )}

                        <div className="mt-5 pt-4 border-t border-slate-100 space-y-2 text-xs text-slate-500">
                            <div className="flex items-center justify-between">
                                <span className="flex items-center gap-1.5">
                                    <Calendar size={11} />
                                    Creado
                                </span>
                                <span className="text-slate-700 font-medium">
                                    {new Date(segment.createdAt).toLocaleDateString(
                                        "es-AR",
                                        {
                                            day: "numeric",
                                            month: "long",
                                            year: "numeric",
                                        },
                                    )}
                                </span>
                            </div>
                            {lastUsedAt && (
                                <div className="flex items-center justify-between">
                                    <span className="flex items-center gap-1.5">
                                        <Sparkles size={11} />
                                        Último uso
                                    </span>
                                    <span className="text-slate-700 font-medium">
                                        {new Date(lastUsedAt).toLocaleDateString(
                                            "es-AR",
                                            {
                                                day: "numeric",
                                                month: "long",
                                            },
                                        )}
                                    </span>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Campañas que lo usan */}
                    <div className="lg:col-span-2 bg-white rounded-2xl border border-slate-100 shadow-sm p-5">
                        <div className="flex items-center justify-between mb-4">
                            <div className="flex items-center gap-2">
                                <div className="p-1.5 rounded-lg bg-emerald-50 text-emerald-600">
                                    <Target size={14} />
                                </div>
                                <h2 className="text-sm font-bold text-slate-900">
                                    Campañas que usan este segmento
                                </h2>
                                <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-slate-100 text-slate-600">
                                    {campaigns.length}
                                </span>
                            </div>
                        </div>

                        {campaigns.length === 0 ? (
                            <div className="text-center py-10 bg-slate-50 rounded-xl">
                                <p className="text-sm text-slate-500 mb-3">
                                    Ninguna campaña usa este segmento todavía
                                </p>
                                <Link to="/objectives/new">
                                    <Button variant="primary">
                                        <Send size={14} />
                                        Crear primera campaña
                                    </Button>
                                </Link>
                            </div>
                        ) : (
                            <div className="space-y-2">
                                {campaigns.map((c) => {
                                    const presetMeta = PRESET_BY_KEY[c.preset];
                                    return (
                                        <Link
                                            key={c.id}
                                            to={`/objectives/${c.id}`}
                                            className="block p-3 rounded-xl border border-slate-100 hover:border-violet-300 hover:bg-violet-50/30 transition-all group"
                                        >
                                            <div className="flex items-center gap-3">
                                                <div className="text-2xl flex-shrink-0">
                                                    {c.emoji}
                                                </div>
                                                <div className="flex-1 min-w-0">
                                                    <div className="flex items-center gap-2 flex-wrap mb-0.5">
                                                        <h3 className="text-sm font-semibold text-slate-900 truncate">
                                                            {c.name}
                                                        </h3>
                                                        <span
                                                            className={`text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded ${
                                                                STATUS_META[c.status]?.classes ??
                                                                "bg-slate-100 text-slate-600"
                                                            }`}
                                                        >
                                                            {STATUS_META[c.status]?.label ?? c.status}
                                                        </span>
                                                    </div>
                                                    <p className="text-[11px] text-slate-500 truncate">
                                                        {presetMeta?.shortDescription}
                                                    </p>
                                                </div>
                                                <div className="text-right flex-shrink-0">
                                                    <p className="text-sm font-bold text-slate-900 tabular-nums">
                                                        {(
                                                            c.metrics
                                                                ?.conversions || 0
                                                        ).toLocaleString("es-AR")}
                                                    </p>
                                                    <p className="text-[10px] text-slate-500">
                                                        conversiones
                                                    </p>
                                                </div>
                                            </div>
                                        </Link>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                </div>

                {/* Sugerencia: editar con IA */}
                <div className="bg-gradient-to-br from-violet-50 to-fuchsia-50 rounded-2xl border border-violet-100 p-5">
                    <div className="flex items-start gap-3">
                        <div className="w-10 h-10 bg-white rounded-xl flex items-center justify-center shadow-sm flex-shrink-0">
                            <MessageSquare size={18} className="text-violet-600" />
                        </div>
                        <div className="flex-1 min-w-0">
                            <p className="text-xs font-semibold text-violet-700 uppercase tracking-wide mb-1">
                                Refinar con IA
                            </p>
                            <p className="text-sm text-slate-700 leading-relaxed">
                                ¿Querés agregar otro criterio o sacar uno? La IA
                                puede ajustar este segmento manteniendo el resto
                                igual. Por ejemplo: "solo los de Belgrano", "que
                                hayan comprado pizza alguna vez", "excluir los
                                que ya están en el segmento VIP".
                            </p>
                            <Link
                                to="/segments/new/ai"
                                state={{ refineSegmentId: id }}
                            >
                                <Button variant="secondary" className="mt-3">
                                    <MessageSquare size={14} />
                                    Refinar este segmento
                                </Button>
                            </Link>
                        </div>
                    </div>
                </div>
            </div>

            {confirmingDelete && segment && (
                <ConfirmDangerModal
                    title="Borrar segmento"
                    description={`¿Borrar el segmento "${segment.name || "sin nombre"}"? Las campañas que lo usan dejan de tener audiencia.`}
                    onConfirm={confirmDelete}
                    onCancel={() => setConfirmingDelete(false)}
                    busy={deleteBusy}
                />
            )}
        </div>
    );
}

export default SegmentDashboardPage;
